/**
 * SQL generation for the browser-local cache.
 *
 * Every grid interaction -- sort, filter, page, column statistics -- becomes a
 * SQL string here and runs against the DuckDB-WASM table. These functions are
 * pure so the generated SQL is directly assertable in tests.
 */

import { quoteIdent } from '@/lib/values';
import type { ColumnSchema, DuckDbType } from '@/types/api';
import { classifyType, isNumericClass } from '@/lib/values';
import type { FilterSpec, SortSpec, ViewSpec } from './types';
import {
  groupSelectClause,
  groupedSchema,
  isGroupActive,
  sortForGroupedSchema,
  type GroupState,
} from '@/components/group/groupModel';

/** Escapes a value into a single-quoted SQL string literal. */
export function sqlString(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/**
 * DuckDB type to use for the cached column.
 *
 * Nested types are stored as JSON text: values crossed the wire as JSON, and
 * flattening them keeps the cache reliable. The detail pane parses the JSON
 * back into a tree, so nothing is lost for the user.
 */
export function cacheColumnType(column: ColumnSchema): string {
  const cls = classifyType(column.type);
  switch (cls) {
    case 'nested':
    case 'binary':
      return 'VARCHAR';
    case 'bigint':
      if (column.type === 'DECIMAL') {
        // Preserve declared precision/scale when the engine reported it.
        return /^DECIMAL\s*\(\s*\d+\s*,\s*\d+\s*\)$/i.test(column.typeText.trim())
          ? column.typeText.trim().toUpperCase()
          : 'DOUBLE';
      }
      return column.type;
    case 'temporal':
    case 'number':
    case 'boolean':
      return column.type;
    default:
      return column.type === 'UUID' ? 'UUID' : 'VARCHAR';
  }
}

/** `columns={...}` struct for read_json, giving the cache exact types. */
export function readJsonColumnsClause(columns: readonly ColumnSchema[]): string {
  const entries = columns.map(
    (column) => `${sqlString(column.name)}: ${sqlString(cacheColumnType(column))}`,
  );
  return `{${entries.join(', ')}}`;
}

export function createTableFromJsonSql(
  table: string,
  fileName: string,
  columns: readonly ColumnSchema[],
): string {
  return [
    `CREATE OR REPLACE TABLE ${quoteIdent(table)} AS`,
    `SELECT * FROM read_json(${sqlString(fileName)},`,
    `  columns = ${readJsonColumnsClause(columns)},`,
    `  format = 'newline_delimited')`,
  ].join('\n');
}

function operand(filter: FilterSpec, index: number): string {
  return filter.values[index] ?? '';
}

/** Renders one filter as a boolean SQL expression. */
export function filterExpression(filter: FilterSpec, type: DuckDbType): string {
  const col = quoteIdent(filter.column);
  const cls = classifyType(type);
  const numeric = isNumericClass(cls);
  const literal = (index: number): string => {
    const raw = operand(filter, index);
    if (numeric && /^-?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(raw.trim())) return raw.trim();
    return sqlString(raw);
  };

  switch (filter.operator) {
    case 'eq':
      return `${col} = ${literal(0)}`;
    case 'neq':
      // NULL-safe: `<>` alone silently drops NULL rows, which reads as data loss.
      return `(${col} IS DISTINCT FROM ${literal(0)})`;
    case 'lt':
      return `${col} < ${literal(0)}`;
    case 'lte':
      return `${col} <= ${literal(0)}`;
    case 'gt':
      return `${col} > ${literal(0)}`;
    case 'gte':
      return `${col} >= ${literal(0)}`;
    case 'like':
      // Explicit LIKE: the user's wildcards are the point, so they are NOT
      // escaped. The value is still a quoted literal, so it cannot break out.
      return `CAST(${col} AS VARCHAR) ILIKE ${sqlString(operand(filter, 0))}`;
    case 'notLike':
      return `(${col} IS NULL OR CAST(${col} AS VARCHAR) NOT ILIKE ${sqlString(
        operand(filter, 0),
      )})`;
    case 'contains':
      return `CAST(${col} AS VARCHAR) ILIKE ${sqlString(`%${escapeLike(operand(filter, 0))}%`)}`;
    case 'notContains':
      return `(${col} IS NULL OR CAST(${col} AS VARCHAR) NOT ILIKE ${sqlString(
        `%${escapeLike(operand(filter, 0))}%`,
      )})`;
    case 'startsWith':
      return `CAST(${col} AS VARCHAR) ILIKE ${sqlString(`${escapeLike(operand(filter, 0))}%`)}`;
    case 'endsWith':
      return `CAST(${col} AS VARCHAR) ILIKE ${sqlString(`%${escapeLike(operand(filter, 0))}`)}`;
    case 'isNull':
      return `${col} IS NULL`;
    case 'isNotNull':
      return `${col} IS NOT NULL`;
    case 'isEmpty':
      return `${col} = ''`;
    case 'between':
      return `${col} BETWEEN ${literal(0)} AND ${literal(1)}`;
    case 'in': {
      // An empty IN list is `FALSE`, not `IN ()`, which is a syntax error.
      if (filter.values.length === 0) return 'FALSE';
      const list = filter.values.map((_, index) => literal(index)).join(', ');
      return `${col} IN (${list})`;
    }
    case 'notIn': {
      if (filter.values.length === 0) return 'TRUE';
      const list = filter.values.map((_, index) => literal(index)).join(', ');
      // NULL-safe, for the same reason as `neq`.
      return `(${col} IS NULL OR ${col} NOT IN (${list}))`;
    }
    default:
      return 'TRUE';
  }
}

function escapeLike(value: string): string {
  return value.replace(/([%_\\])/g, '\\$1');
}

/**
 * Joins filters with their per-row `and`/`or`.
 *
 * Plain SQL precedence applies -- AND binds tighter than OR -- which matches
 * what the Text-mode view of the same rows shows, so the builder and the
 * textarea never disagree about meaning.
 */
export function whereClause(
  filters: readonly FilterSpec[],
  types: ReadonlyMap<string, DuckDbType>,
): string {
  if (filters.length === 0) return '';

  const body = filters
    .map((filter, index) => {
      const sql = filterExpression(filter, types.get(filter.column) ?? 'VARCHAR');
      if (index === filters.length - 1) return sql;
      const joiner = (filters[index]?.joiner ?? 'and') === 'or' ? ' OR ' : ' AND ';
      return `${sql}${joiner}`;
    })
    .join('');

  return ` WHERE ${body}`;
}

export function orderByClause(sort: readonly SortSpec[]): string {
  if (sort.length === 0) return '';
  const parts = sort.map(
    // NULLS LAST on ascending matches what analysts expect from a spreadsheet.
    (spec) =>
      `${quoteIdent(spec.column)} ${spec.direction === 'desc' ? 'DESC' : 'ASC'} NULLS ${
        spec.direction === 'desc' ? 'FIRST' : 'LAST'
      }`,
  );
  return ` ORDER BY ${parts.join(', ')}`;
}

export interface ViewSqlOptions {
  /** Table name for the local cache, or a subquery for the remote fallback. */
  readonly source: string;
  readonly types: ReadonlyMap<string, DuckDbType>;
}

/** Text-mode predicate wins over the builder rows when present. */
function predicateClause(spec: Pick<ViewSpec, 'filters' | 'predicate'>, types: ViewSqlOptions['types']): string {
  const raw = spec.predicate?.trim() ?? '';
  if (raw !== '') return ` WHERE ${raw}`;
  return whereClause(spec.filters, types);
}

export function buildViewSql(spec: ViewSpec, options: ViewSqlOptions): string {
  const where = predicateClause(spec, options.types);
  const limit = ` LIMIT ${Math.max(0, Math.trunc(spec.limit))}`;
  const offset = spec.offset > 0 ? ` OFFSET ${Math.trunc(spec.offset)}` : '';

  // filter -> group -> sort. Sorting after the grouping is what lets a user
  // order by an aggregate.
  if (spec.group && isGroupActive(spec.group)) {
    const { select, groupBy } = groupSelectClause(spec.group, options.types);
    const schema = groupedSchema(spec.group, options.types);
    const order = orderByClause(sortForGroupedSchema(spec.sort, schema));
    return `SELECT ${select} FROM ${options.source}${where}${groupBy}${order}${limit}${offset}`;
  }

  const order = orderByClause(spec.sort);
  return `SELECT * FROM ${options.source}${where}${order}${limit}${offset}`;
}

export function buildCountSql(
  filters: readonly FilterSpec[],
  options: ViewSqlOptions,
  predicate?: string | null,
  group?: GroupState | null,
): string {
  const where = predicateClause({ filters, predicate: predicate ?? null }, options.types);

  // A grouped result's row count is the number of groups, not the number of
  // source rows.
  if (group && isGroupActive(group)) {
    const { select, groupBy } = groupSelectClause(group, options.types);
    return `SELECT count(*) AS n FROM (SELECT ${select} FROM ${options.source}${where}${groupBy}) AS _g`;
  }

  return `SELECT count(*) AS n FROM ${options.source}${where}`;
}

/** Wraps the user's original SQL so remote-mode sorts/filters reuse the server. */
export function remoteSource(originalSql: string): string {
  const trimmed = originalSql.trim().replace(/;\s*$/, '');
  return `(\n${trimmed}\n) AS _q`;
}

export function localTableName(queryId: string): string {
  const safe = queryId.replace(/[^A-Za-z0-9_]/g, '_').slice(0, 48);
  return `res_${safe}`;
}
